// Shared UI pieces for the FAQ list and article-detail pages: the page
// container, loading/error states, the app-bar header (back button + title),
// and the email-support footer.

import React from "react";
import styled from "styled-components";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";

import { sendEmail } from "./faqBridge";
import { t } from "./faqStrings";

export const Container = styled.div`
  background-color: var(--bg-color);
  width: 100%;
  min-height: 100vh;
  color: var(--text-color_sub1);
`;

export const Spinner = () => (
  <div className="faq-state">
    <span className="faq-spinner" />
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="faq-state error">
    {message}
    {onRetry ? (
      <button className="faq-retry" onClick={onRetry}>
        Retry
      </button>
    ) : null}
  </div>
);

const BackIcon = () => (
  <svg width="1.75rem" height="1.75rem" viewBox="0 0 28 28">
    <path
      fill="var(--text-color_sub1)"
      fillRule="nonzero"
      d="M8.53281786,14.9333333 C8.06615119,14.4666667 8.06615119,13.8833333 8.41615119,13.4166667 L8.53281786,13.3 L16.8161512,5.01666667 C17.2828179,4.55 17.9828179,4.55 18.4494845,5.01666667 C18.9161512,5.48333333 18.9161512,6.06666667 18.5661512,6.53333333 L18.4494845,6.65 L10.9828179,14.1166667 L18.4494845,21.5833333 C18.9161512,22.05 18.9161512,22.6333333 18.5661512,23.1 L18.4494845,23.2166667 C17.9828179,23.6833333 17.3994845,23.6833333 16.9328179,23.3333333 L16.8161512,23.2166667 L8.53281786,14.9333333 Z"
    />
  </svg>
);

// Fixed app bar with a back button (onBack) and the localized FAQ title.
// Renders the bar plus the spacer that offsets the fixed positioning.
// `showBack` toggles the back/close button; when false an equally-sized
// placeholder keeps the title centered (mirrors the right-side placeholder).
export const FaqHeader = ({
  locale,
  onBack,
  showBack = true,
  variant = "list",
}) => (
  <>
    <AppBar
      className={
        "faq-header faq-header--" +
        variant +
        (showBack ? "" : " faq-header--noback")
      }
      style={{
        backgroundColor: "var(--bg-color)",
        position: "fixed",
        minHeight: "2.75rem",
        boxShadow: "none",
      }}
    >
      <Toolbar
        style={{
          minHeight: "2.75rem",
          marginTop: "4px",
          padding: "0px 1.1875rem",
        }}
      >
        {showBack ? (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            aria-label="back"
            style={{ padding: "0px", marginLeft: "0px" }}
            size="large"
          >
            <BackIcon />
          </IconButton>
        ) : (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="placeholder"
            style={{ padding: "0px", marginLeft: "0px" }}
            size="large"
          >
            <svg width="1.75rem" height="1.75rem" viewBox="0 0 28 28"></svg>
          </IconButton>
        )}
        <div className="notice-header" style={{ margin: "0px auto" }}>
          {t("faqTitle", locale)}
        </div>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="placeholder"
          style={{ padding: "0px", marginLeft: "0px" }}
          size="large"
        >
          <svg width="1.625rem" height="1.625rem" viewBox="0 0 26 26"></svg>
        </IconButton>
      </Toolbar>
    </AppBar>
    <div className="header-size-element" />
  </>
);

// Debug-only fixed bar pinned to the bottom showing the live WebView viewport
// size (width × height in CSS px), updated on resize. Used to test width-driven
// behavior inside the native WebView. Hidden on the production build: it renders
// only when REACT_APP_BUCKET_NAME === "test-notice" (local dev + staging/test
// deploys), matching the same prod/test split faqConfig uses.
const IS_TEST_BUILD = process.env.REACT_APP_BUCKET_NAME === "test-notice";

export const DebugViewport = () => {
  const [size, setSize] = React.useState({
    w: window.innerWidth,
    h: window.innerHeight,
  });

  React.useEffect(() => {
    if (!IS_TEST_BUILD) return undefined;
    const onResize = () =>
      setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  if (!IS_TEST_BUILD) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: "4px 8px",
        textAlign: "center",
        fontSize: "12px",
        fontFamily: "monospace",
        color: "var(--text-color_sub1)",
        backgroundColor: "var(--bg-color)",
        borderTop: "1px solid var(--text-color_sub1)",
        opacity: 0.85,
        pointerEvents: "none",
      }}
    >
      {size.w} × {size.h} px
    </div>
  );
};

// Email-support footer. `category` is sent to the app's mail composer
// ("Common" on the list, the section name on the detail page).
export const EmailFooter = ({ locale, category }) => (
  <div className="faq-footer">
    <p
      className="faq-email-msg"
      dangerouslySetInnerHTML={{ __html: t("emailSupportMsg", locale) }}
    />
    <button className="faq-email-btn" onClick={() => sendEmail(category)}>
      {t("emailSupportBtn", locale)}
    </button>
  </div>
);
